import { environment } from '@/config/environment'
import { iamAuth } from '@/lib/firebase'

/**
 * Thin fetch wrapper that:
 *  - prefixes the URL with `environment.apiBaseURL` (already includes
 *    the `/api/v1` segment — versioning happens here, not at callers)
 *  - attaches a fresh IAM bearer token on every request
 *  - parses JSON; throws an `ApiError` on non-2xx with the parsed body
 *
 * Why bearer-only (no cookies)? Web app is cross-origin to the
 * functions URL, and we want zero CSRF surface. iOS uses the same
 * shape; both clients share verification on the backend.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

type FetchOptions = Omit<RequestInit, 'body'> & {
  body?: unknown
}

async function authedFetch(path: string, options: FetchOptions = {}): Promise<Response> {
  const url = path.startsWith('http')
    ? path
    : `${environment.apiBaseURL}${path.startsWith('/') ? path : `/${path}`}`

  const headers = new Headers(options.headers)
  if (!headers.has('Content-Type') && options.body !== undefined) {
    headers.set('Content-Type', 'application/json')
  }

  const user = iamAuth.currentUser
  if (user) {
    const token = await user.getIdToken()
    headers.set('Authorization', `Bearer ${token}`)
  }

  return fetch(url, {
    ...options,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  })
}

async function parseOrThrow<T>(res: Response): Promise<T> {
  const text = await res.text()
  let body: unknown
  try {
    body = text.length > 0 ? JSON.parse(text) : undefined
  } catch {
    body = text
  }
  if (!res.ok) {
    const message =
      (typeof body === 'object' && body !== null && 'error' in body && typeof body.error === 'string'
        ? body.error
        : null) ?? `HTTP ${res.status}`
    throw new ApiError(message, res.status, body)
  }
  return body as T
}

export const api = {
  get: async <T,>(path: string): Promise<T> => parseOrThrow<T>(await authedFetch(path)),
  post: async <T,>(path: string, body?: unknown): Promise<T> =>
    parseOrThrow<T>(await authedFetch(path, { method: 'POST', body })),
  put: async <T,>(path: string, body?: unknown): Promise<T> =>
    parseOrThrow<T>(await authedFetch(path, { method: 'PUT', body })),
  delete: async <T,>(path: string): Promise<T> =>
    parseOrThrow<T>(await authedFetch(path, { method: 'DELETE' })),
}
